import { useTranslation } from "react-i18next"
import { CaretDown, Check } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Chip } from "@/components/ui/chip"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"
import { getLocalizedQualification } from "@/modules/shift-plan-overview/helpers/shift-plan-helper"
import { useShiftPlanFiltersStore } from "@/modules/shift-plan-overview/stores/useShiftPlanFiltersStore"

export const QualificationFilter = () => {
  const { t } = useTranslation()

  const qualifications = useShiftPlanFiltersStore((state) => state.filters.qualifications)
  const setQualifications = useShiftPlanFiltersStore((state) => state.actions.setQualifications)
  const availableQualifications = useShiftPlanFiltersStore((state) => state.availableQualifications)
  const isLoading = useShiftPlanFiltersStore((state) => state.isLoading)
  const count = qualifications.length

  const handleCheckChange = (item: string) => {
    const index = qualifications.indexOf(item)
    const newQualifications = [...qualifications]

    if (index < 0) newQualifications.push(item)
    else newQualifications.splice(index, 1)

    setQualifications(newQualifications)
  }

  const handleReset = () => {
    setQualifications([])
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger
        asChild
        disabled={isLoading}
        className={cn(
          "aria-checked:border-on-primary-surface aria-checked:bg-primary-surface aria-checked:text-on-primary-surface aria-checked:hover:enabled:bg-primary-surface-hovered aria-checked:focus:enabled:border-on-primary-surface outline-hidden hover:cursor-pointer",
          isLoading && "cursor-not-allowed!",
        )}>
        <Chip
          data-testid="shift-plan-qualification-filter-dropdown"
          className="h-8 gap-0.5"
          checked={count > 0}
          type="button">
          <div className={`overflow-hidden transition-all duration-200 ${count > 0 ? "w-5" : "w-0"}`}>
            <Check size={20} />
          </div>
          <span className="px-1.5">
            {t("shiftPlanOverview.filters.qualification") + (count > 0 ? ` (${count})` : ``)}
          </span>
          <CaretDown size={20} />
        </Chip>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        data-testid="shift-plan-qualification-filter-content"
        className="absolute -top-1 grid gap-2 rounded-md border-none p-2 leading-5">
        <ScrollArea className="max-h-80">
          <div className="grid gap-0.5">
            {availableQualifications.map((item) => (
              <DropdownMenuCheckboxItem
                onSelect={(e) => e.preventDefault()}
                className="h-9"
                key={item}
                checked={qualifications.includes(item)}
                data-testid={`shift-qualification-filter-${item.toLowerCase()}`}
                onCheckedChange={() => handleCheckChange(item)}>
                {getLocalizedQualification(t, item)}
              </DropdownMenuCheckboxItem>
            ))}
          </div>
        </ScrollArea>
        <DropdownMenuSeparator className="bg-border -mx-2 my-0" />
        <div className="flex justify-end">
          <Button
            onClick={handleReset}
            data-testid="shift-plan-reset-qualification-filter"
            variant="tertiary"
            className="h-9 rounded-full px-2 py-0 leading-5 font-medium">
            {t("common.button.reset")}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
